import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Offcanvas, Form, Image, Card, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import exhibitionService from '../services/exhibitionService';
import countryService from '../services/countryService';
import config from '../../config';

const CountryExhibitionsPage = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();
  const [showDrawer, setShowDrawer] = useState(false);
  const [exhibitions, setExhibitions] = useState([]);
  const [countryInfo, setCountryInfo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: null,
    countryId: countryId
  });

  const loadCountryInfo = useCallback(async () => {
    try {
      const response = await countryService.getCountryById(countryId);
      console.log('Country info response:', response);
      
      // Yeni format: {data: {...}, log: {...}, message: '...'}
      let countryData = response;
      
      // Eğer response.data varsa (yeni format), onu kullan
      if (response && response.data) {
        countryData = response.data;
        console.log('Live Deployment Log:', response.log);
        console.log('Server:', response.log?.server);
        console.log('Timestamp:', response.log?.timestamp);
      }
      
      console.log('Country data:', countryData);
      setCountryInfo(countryData);
    } catch (error) {
      console.error('Failed to load country info:', error);
    }
  }, [countryId]);

  const loadExhibitions = useCallback(async () => {
    try {
      const data = await exhibitionService.getExhibitionsByCountry(countryId);
      console.log('Exhibitions data:', data);
      console.log('Data type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      
      // Eğer data bir dizi değilse, boş dizi kullan
      if (Array.isArray(data)) {
      setExhibitions(data);
      } else {
        console.error('Exhibitions data is not an array:', data);
        setExhibitions([]);
      }
    } catch (error) {
      console.error('Failed to load exhibitions:', error);
      setExhibitions([]);
    }
  }, [countryId]);

  useEffect(() => {
    loadCountryInfo();
    loadExhibitions();
  }, [loadCountryInfo, loadExhibitions]);

  const handleDrawerClose = () => setShowDrawer(false);
  const handleDrawerShow = () => setShowDrawer(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image_url: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('countryId', formData.countryId);
      if (formData.image_url) {
        formDataToSend.append('image_url', formData.image_url);
      }

      await exhibitionService.addExhibition(formDataToSend);
      handleDrawerClose();
      loadExhibitions();
      loadCountryInfo(); // Refresh country info to update total photos
      setFormData({ 
        title: '', 
        description: '', 
        image_url: null, 
        countryId: countryId 
      });
    } catch (error) {
      console.error('Failed to add exhibition:', error);
    }
  };

  const handleDelete = async (exhibitionId) => {
    if (window.confirm('Are you sure you want to delete this exhibition?')) {
      try {
        await exhibitionService.deleteExhibition(exhibitionId);
        loadExhibitions(); // Refresh the list after deletion
        loadCountryInfo(); // Refresh country info to update total photos
      } catch (error) {
        console.error('Failed to delete exhibition:', error);
      }
    }
  };

  const handleManagePhotos = (exhibition) => {
    navigate(`/admin/exhibitions/photos/${exhibition.id}`);
  };

  return (
    <Container fluid className="mt-5">
      {/* Country Info Card */}
      {countryInfo && (
        <Card className="mb-4">
          <Row className="g-0">
            <Col md={3}>
              <Image 
                src={countryInfo.cover_image ? (() => {
                  // Eğer path zaten /api ile başlıyorsa, sadece domain ekle
                  if (countryInfo.cover_image.startsWith('/api/')) {
                    return `https://tekstas.com${countryInfo.cover_image}`;
                  }
                  // Eğer path /uploads ile başlıyorsa, API_URL ile birleştir
                  else if (countryInfo.cover_image.startsWith('/uploads/')) {
                    return `${config.API_URL}${countryInfo.cover_image}`;
                  }
                  // Diğer durumlar için API_URL ile birleştir
                  else {
                    return `${config.API_URL}${countryInfo.cover_image}`;
                  }
                })() : null}
                style={{ 
                  width: '100%', 
                  height: '200px', 
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Col>
            <Col md={9}>
              <Card.Body>
                <Card.Title className="h3">{countryInfo.name}</Card.Title>
                <Card.Text>
                  <strong>Total Photos:</strong> {countryInfo.totalPhotos || 0}
                </Card.Text>
                <Card.Text className="text-muted">
                  <small>Country ID: {countryInfo.id}</small>
                </Card.Text>
              </Card.Body>
            </Col>
          </Row>
        </Card>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Country Exhibitions</h2>
        <Button variant="primary" onClick={handleDrawerShow}>
          Add New Exhibition
        </Button>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Image</th>
              <th>Title</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {exhibitions && exhibitions.map((exhibition) => (
              <tr key={exhibition.id}>
                <td>
                  <Image 
                    src={exhibition.image_url ? `${config.API_URL.replace('/api', '')}${exhibition.image_url}` : null}
                    style={{ height: '100px', objectFit: 'cover' }}
                    thumbnail
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </td>
                <td>{exhibition.title}</td>
                <td>{exhibition.description}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => handleManagePhotos(exhibition)}
                      style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                    >
                      Manage Photos
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm"
                      onClick={() => handleDelete(exhibition.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

        </Table>
      </div>

      <Offcanvas show={showDrawer} onHide={handleDrawerClose} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Add New Exhibition</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control 
                type="text" 
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter exhibition title" 
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter exhibition description" 
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control 
                type="file" 
                onChange={handleImageChange}
                accept="image/*"
                required
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit">
                Save Exhibition
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
};

export default CountryExhibitionsPage;